import React, { useMemo, useState } from "react";
import {
  LifeBuoy, Mail, Search, ChevronDown,
  Clock3, ShieldCheck,
} from "lucide-react";

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none";
const labelCls = "block text-xs mb-1 text-slate-600 dark:text-slate-300 font-medium";

const faqItems = [
  {
    question: "Como conectar minha conta do Google Ads?",
    answer: "Vá até a área de Contas Google Ads no menu lateral, conecte seu perfil e depois selecione a conta desejada ao criar ou editar um SobeScript.",
  },
  {
    question: "Por que minha conversão não apareceu no Google Ads?",
    answer: "Isso pode acontecer por atraso de processamento, GCLID ausente, configuração incompleta da ação de conversão ou falha no webhook. Confira os logs e a configuração da integração.",
  },
  {
    question: "Como encontro os logs dos webhooks recebidos?",
    answer: "Você pode validar os registros nas áreas de vendas, logs e integrações para identificar payload recebido, status do processamento e mensagens de erro.",
  },
  {
    question: "O suporte ajuda com configuração técnica?",
    answer: "Sim. Envie o máximo de contexto possível, como UUID do script, mensagem de erro, plataforma envolvida e o que você já testou.",
  },
];

// ─── FAQ Item ─────────────────────────────────────────────────────────────────

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.question}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 text-sm leading-relaxed text-slate-600 dark:text-slate-300 border-t border-slate-100 dark:border-slate-800 pt-3">
          {item.answer}
        </div>
      )}
    </div>
  );
}

// ─── Info Card ────────────────────────────────────────────────────────────────

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
      <div className="mb-3 w-10 h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
        <Icon className="w-5 h-5 text-slate-600 dark:text-slate-300" />
      </div>
      <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">{title}</h3>
      <div className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{children}</div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function SupportPage() {
  const [query, setQuery] = useState("");
  const [openIndex, setOpenIndex] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", subject: "", category: "", priority: "", message: "" });

  const filteredFaq = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return faqItems;
    return faqItems.filter((item) => item.question.toLowerCase().includes(term) || item.answer.toLowerCase().includes(term));
  }, [query]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    console.log("Ticket payload:", form);
    alert("Chamado enviado com sucesso!");
    setForm({ name: "", email: "", subject: "", category: "", priority: "", message: "" });
  }

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HERO — identidade visual da página, mantém gradiente */}
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 p-6 text-white">
          <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 h-32 w-32 rounded-full bg-white/10 blur-2xl pointer-events-none" />

          <div className="relative grid gap-6 lg:grid-cols-[1.25fr_.75fr] lg:items-center">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-sm font-medium backdrop-blur">
                <LifeBuoy className="w-4 h-4" /> Central de Suporte SobeVenda
              </div>
              <h1 className="max-w-2xl text-2xl font-bold tracking-tight sm:text-3xl">
                Precisa de ajuda com scripts, integrações, webhooks ou Google Ads?
              </h1>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-emerald-50">
                Encontre respostas rápidas, abra um chamado e fale com o suporte de forma organizada.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-emerald-700">
                  <Clock3 className="w-4 h-4" /> Resposta em horário comercial
                </div>
                <div className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-4 py-2 text-sm font-medium backdrop-blur">
                  <ShieldCheck className="w-4 h-4" /> Atendimento técnico e operacional
                </div>
              </div>
            </div>

            {/* Busca rápida */}
            <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-md border border-white/15">
              <div className="rounded-xl bg-white dark:bg-slate-900 p-4 shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                    <Search className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Busca rápida</p>
                    <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Como podemos te ajudar hoje?</h2>
                  </div>
                </div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar por integração, conversão, webhook..."
                    className={`${inputCls} pl-9`} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Canais de contato */}
        <div className="grid gap-4 lg:grid-cols-2">
          <InfoCard icon={Mail} title="Suporte por e-mail">
            Atendimento centralizado por e-mail para garantir mais organização e contexto em cada caso.
            <div className="mt-3 font-semibold text-slate-900 dark:text-slate-100">suporte@sobevenda.com.br</div>
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Ideal para dúvidas técnicas, erros de integração, scripts, webhooks e Google Ads.
            </div>
          </InfoCard>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-5">
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">Em breve</h3>
            <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              Tutoriais passo a passo e novos canais de atendimento serão adicionados.
            </p>
          </div>
        </div>

        {/* FAQ + Fale com suporte */}
        <div className="grid gap-6 xl:grid-cols-[1fr_.72fr]">

          {/* FAQ */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-1">Perguntas frequentes</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Respostas rápidas para as dúvidas mais comuns.</p>
            <div className="space-y-3">
              {filteredFaq.length > 0 ? (
                filteredFaq.map((item, index) => (
                  <FaqItem key={item.question} item={item} isOpen={openIndex === index}
                    onToggle={() => setOpenIndex(openIndex === index ? -1 : index)} />
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-6 text-sm text-slate-500 dark:text-slate-400">
                  Nenhum resultado encontrado para sua busca.
                </div>
              )}
            </div>
          </div>

          {/* Fale com suporte */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5">
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-1">Fale com o suporte</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">O atendimento acontece somente por e-mail.</p>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4">
              <div className="mb-3 w-10 h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-center">
                <Mail className="w-5 h-5 text-slate-600 dark:text-slate-300" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
                Envie sua dúvida para nosso e-mail
              </h3>
              <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300 mb-3">
                Para dúvidas técnicas, erros de integração, scripts, webhooks e Google Ads, envie sua mensagem com o máximo de contexto possível.
              </p>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
                suporte@sobevenda.com.br
              </div>
              <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Inclua, sempre que possível, o script, mensagem de erro, plataforma envolvida e o que você já testou.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}